// crewbit-admin/src/hooks/useUpdateScheduleEntry.ts
//
// Schedule Module Phase B.1 — paired hook for `update_schedule_entry` RPC
// (Migration 312 + A13 corrections in 313).
// Spec: docs/references/schedule/SCHEDULE_MODULE_WIRING_v1.md §4.2.
//
// Mirror of crewbit-mobile/hooks/useUpdateScheduleEntry.ts.

import { useCallback } from 'react';
import { callScheduleRpc, type ScheduleRpcResult } from '../lib/scheduleRpc';
import type { ConflictDetail } from '../lib/errorMap';

/**
 * Free-form updates patch — keys map to schedule_entries columns. Server validates
 * which keys the caller may update + rejects shift-projected mutation classes.
 */
export interface UpdateScheduleEntryPatch {
  title?: string;
  notes?: string | null;
  color?: string | null;
  location_name?: string | null;
  location_address?: string | null;
  location_lat?: number | null;
  location_lng?: number | null;
  capacity?: number | null;
  start_at?: string;
  end_at?: string;
  assigned_to?: string[] | null;
  status?: string;
  is_skipped?: boolean;
  recurrence_end?: string | null;
}

export interface UpdateScheduleEntryInput {
  entryId: string;
  updates: UpdateScheduleEntryPatch;
  idempotencyKey?: string;
  force?: boolean;
}

export type UpdateScheduleEntryResult = ScheduleRpcResult<{
  success: true;
  entry_id?: string;
  conflicts?: ConflictDetail[];
  already_updated?: boolean;
}>;

export function useUpdateScheduleEntry() {
  return useCallback(
    async (input: UpdateScheduleEntryInput): Promise<UpdateScheduleEntryResult> => {
      return callScheduleRpc<{
        success: true;
        entry_id?: string;
        conflicts?: ConflictDetail[];
        already_updated?: boolean;
      }>('update_schedule_entry', {
        p_entry_id: input.entryId,
        p_updates: input.updates,
        p_idempotency_key: input.idempotencyKey ?? null,
        p_force: input.force ?? false,
      });
    },
    [],
  );
}
